/**
 * <s-dust>: a sparse layer of warm dust motes drifting in the hero light. It draws
 * on its own <canvas>, reads the sunrise strength from the closest
 * <s-hero-reveal>'s --sun (0..1), and makes motes brighter inside the corner
 * light shaft. Few particles, capped DPR, paused off screen or in a hidden tab;
 * nothing renders with reduced motion.
 */
class SDust extends HTMLElement {
  #last = 0;
  #canvas = null;
  #ctx = null;
  #motes = [];
  #frame = 0;
  #visible = false;
  #io = null;
  #ro = null;
  #w = 0;
  #h = 0;
  #dpr = 1;
  #onVisibility = () => this.#wake();
  #tick = (time) => {
    this.#frame = 0;
    if (!this.#visible || document.hidden) return;
    // Perf (2026-10-29 audit): ~30 fps; motion is scaled by the elapsed time so the drift speed is unchanged.
    if (time - this.#last < 32) { this.#frame = requestAnimationFrame(this.#tick); return; }
    const step = this.#last ? Math.min((time - this.#last) / 16.7, 4) : 1;
    this.#last = time;
    const host = this.closest('s-hero-reveal');
    const sun = parseFloat(host?.style.getPropertyValue('--sun')) || 0;
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, this.#w, this.#h);
    const strength = 0.18 + sun * 0.82;
    for (const mote of this.#motes) {
      mote.y -= mote.speed * step;
      mote.x += (Math.sin(time / mote.period + mote.phase) * 0.12 + mote.wind) * step;
      if (mote.y < -4) { mote.y = this.#h + 4; mote.x = Math.random() * this.#w; }
      if (mote.x < -4) mote.x = this.#w + 4;
      if (mote.x > this.#w + 4) mote.x = -4;
      const shaft = Math.max(0, 1 - Math.hypot((this.#w - mote.x) / this.#w, mote.y / this.#h) * 0.9);
      const twinkle = 0.65 + Math.sin(time / 900 + mote.phase) * 0.35;
      const alpha = mote.alpha * strength * twinkle * (0.35 + shaft * 0.9);
      ctx.beginPath();
      ctx.fillStyle = `rgba(255, 226, 212, ${Math.min(alpha, 0.85).toFixed(3)})`;
      ctx.arc(mote.x, mote.y, mote.r, 0, Math.PI * 2);
      ctx.fill();
    }
    this.#frame = requestAnimationFrame(this.#tick);
  };

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#canvas = document.createElement('canvas');
    this.#canvas.setAttribute('aria-hidden', 'true');
    this.appendChild(this.#canvas);
    this.#ctx = this.#canvas.getContext('2d');
    if (!this.#ctx) return;
    this.#ro = new ResizeObserver(() => this.#resize());
    this.#ro.observe(this);
    this.#resize();
    this.#io = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#wake();
    });
    this.#io.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#ro?.disconnect();
    this.#io?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #resize() {
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#w = this.clientWidth;
    this.#h = this.clientHeight;
    this.#canvas.width = Math.round(this.#w * this.#dpr);
    this.#canvas.height = Math.round(this.#h * this.#dpr);
    this.#ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    const count = this.#w < 768 ? 18 : 38;
    this.#motes = Array.from({ length: count }, () => ({
      x: Math.random() * this.#w,
      y: Math.random() * this.#h,
      r: 0.5 + Math.random() * 1.4,
      alpha: 0.25 + Math.random() * 0.55,
      speed: 0.05 + Math.random() * 0.18,
      wind: -0.04 - Math.random() * 0.06,
      period: 2200 + Math.random() * 2600,
      phase: Math.random() * Math.PI * 2,
    }));
  }

  #wake() {
    if (!this.#frame && this.#visible && !document.hidden) this.#frame = requestAnimationFrame(this.#tick);
  }
}

if (!customElements.get('s-dust')) customElements.define('s-dust', SDust);
